import React, { useContext, useMemo, useState } from 'react';
import DatePicker from 'components/molecules/common/Form/DatePicker';
import { FormData } from './OptimizePlan';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import dayjs from 'dayjs';
import { useOptimizeOptions } from 'hooks/tradePromoOptimization/useOptimizeOptions';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import styled from '@emotion/styled';
import { TPODataContext } from 'contexts/TPOContext';
import { sendNotification } from 'utils/helperMethods';
import { InputTypes } from './OptimizePlanTable/OptimizeTableContent';
interface IProps {
    setFormData?: React.Dispatch<React.SetStateAction<FormData>>;
    formData: FormData;
    setMaxPromoWeeks?: React.Dispatch<React.SetStateAction<number>>;
    maxPromoWeeks?: number;
    /** Promotion count from scope (replaces merchMDFPromoCount when in Optimize Plan modal). */
    promotionTotalCount?: number;
    /** True while promotion count API is loading (show loader next to count). */
    promotionCountLoading?: boolean;
    viewMode?: boolean;
}

export const StyledSelectWithInfiniteScroller = styled(SelectWithInfiniteScroller)`
    .ant-select-selector {
        border-radius: 6px !important;
    }
`;

const OptimizePlanForm: React.FC<IProps> = ({
    setFormData = () => {},
    formData,
    setMaxPromoWeeks,
    maxPromoWeeks,
    promotionTotalCount: promotionTotalCountProp,
    promotionCountLoading = false,
    viewMode = false,
}) => {
    const { optimizeOptions, loading: optimizeOptionsLoading } = useOptimizeOptions();
    const [isExpanded, setIsExpanded] = useState<boolean>(true);
    const { merchMDFPromoCount } = useContext(TPODataContext);
    const displayCount = promotionTotalCountProp ?? 0;

    const formFields = useMemo(
        () => [
            {
                name: 'objective',
                preLabel: 'Objective',
                postLabel: '',
            },
            {
                name: 'start_date',
                preLabel: 'Start Date',
                postLabel: '',
                type: 'date',
            },
            {
                name: 'keep_top',
                preLabel: 'Keep Top',
                postLabel: `out of ${displayCount}`,
            },
            {
                name: 'no_of_concurrent_tprs',
                preLabel: 'No of concurrent TPRs',
                postLabel: '',
            },
            {
                name: 'no_of_concurrent_fds',
                preLabel: 'No of concurrent F&Ds',
                postLabel: '',
            },
            {
                name: 'no_of_concurrent_mdfs',
                preLabel: 'No of concurrent MDFs',
                postLabel: '',
            },
        ],
        [displayCount],
    );

    return (
        <div className="px-3 py-2 bg-[rgba(250,250,250,1)] rounded-lg mb-3">
            <div
                className="flex justify-between items-center cursor-pointer w-full"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="font-semibold">Base Inputs</div>
                <div>
                    {isExpanded ? (
                        <UpOutlined className="!text-purpleFont" />
                    ) : (
                        <DownOutlined className="!text-purpleFont" />
                    )}
                </div>
            </div>
            {isExpanded &&
                (viewMode ? (
                    <div className="flex justify-between">
                        {formFields.map((item) => (
                            <div className="flex flex-col gap-1 w-[17.25rem]">
                                <label className="text-sm flex gap-1 items-center">
                                    {item.preLabel} {!item.postLabel && ':'}{' '}
                                    {item.type === 'date'
                                        ? dayjs
                                              .unix(formData?.[item.name as keyof FormData] as number)
                                              .utc()
                                              .format('MM/DD/YYYY')
                                        : formData?.[item.name as keyof FormData] || '0'}
                                    {item.postLabel ? ` ${item.postLabel}` : ''}
                                </label>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-y-4 gap-x-6 mt-3">
                        {/* Objective */}
                        <div className="flex flex-col gap-1 w-[17.25rem]">
                            <label className="text-sm flex gap-1 items-center">
                                <span className="text-redTriangle">*</span>
                                Objective
                            </label>
                            <StyledSelectWithInfiniteScroller
                                hasMore={false}
                                showSearch={false}
                                filterOption={false}
                                placeholder={optimizeOptionsLoading ? 'Loading...' : 'Select'}
                                maxTagCount="responsive"
                                className="dropdown-height !h-8 !w-full"
                                showArrow
                                loading={optimizeOptionsLoading}
                                disabled={optimizeOptionsLoading}
                                onChange={(val) => {
                                    setFormData((prev) => ({
                                        ...prev,
                                        objective: val,
                                    }));
                                }}
                                value={formData.objective}
                                options={optimizeOptions}
                            />
                        </div>

                        {/* Start Date */}
                        <div className="flex flex-col gap-1 w-[17.25rem]">
                            <label className="text-sm flex gap-1 items-center">
                                <span className="text-redTriangle">*</span>
                                Start Date
                            </label>
                            <DatePicker
                                value={formData.start_date}
                                label={null}
                                onChange={(date: number | null) => {
                                    setFormData((prev) => ({ ...prev, start_date: date }));
                                }}
                                format="MM/DD/YYYY"
                                showToday={true}
                                className="!h-8 !w-full"
                                enabledDates={{ start: dayjs().startOf('day').unix() }}
                                allowedDays={[0]}
                            />
                        </div>

                        {/* Keep Top */}
                        <div className="flex items-center justify-center gap-2 w-[17.25rem]">
                            <label className="text-sm">Keep Top</label>
                            <input
                                type="number"
                                className="w-14 px-2 border border-borderGreyAlt rounded-md focus:outline-none h-8 "
                                placeholder="0"
                                value={formData.keep_top || ''}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    if (val >= 0 && val <= displayCount)
                                        setFormData((prev) => ({
                                            ...prev,
                                            keep_top: val,
                                        }));
                                    else if (val > displayCount) {
                                        sendNotification(
                                            'error',
                                            `Keep Top must be less than or equal to ${displayCount}`,
                                        );
                                    }
                                }}
                            />
                            <span className="flex items-center gap-1">
                                out of{' '}
                                {promotionCountLoading ? <Spin style={{ marginLeft: 4 }} size="small" /> : displayCount}
                            </span>
                        </div>

                        {/* No of concurrent TPRs */}
                        <div className="flex flex-col gap-1 w-[17.25rem]">
                            <label className="text-sm flex gap-1 items-center">
                                <span className="text-redTriangle">*</span>
                                No of concurrent TPRs
                            </label>
                            <input
                                type="number"
                                className="!h-8 !w-full px-2 border border-borderGreyAlt rounded-md focus:outline-none"
                                placeholder="Enter value"
                                onChange={(e) => {
                                    if (Number(e.target.value) >= 0)
                                        setFormData((prev) => ({
                                            ...prev,
                                            no_of_concurrent_tprs: Number(e.target.value),
                                        }));
                                }}
                                value={formData.no_of_concurrent_tprs || ''}
                            />
                        </div>

                        {/* No of concurrent F&Ds */}
                        <div className="flex flex-col gap-1 w-[17.25rem]">
                            <label className="text-sm flex gap-1 items-center">
                                <span className="text-redTriangle">*</span>
                                No of concurrent F&Ds
                            </label>
                            <input
                                type="number"
                                className="!h-8 !w-full px-2 border border-borderGreyAlt rounded-md focus:outline-none"
                                placeholder="Enter value"
                                onChange={(e) => {
                                    if (Number(e.target.value) >= 0)
                                        setFormData((prev) => ({
                                            ...prev,
                                            no_of_concurrent_fds: Number(e.target.value),
                                        }));
                                }}
                                value={formData.no_of_concurrent_fds || ''}
                            />
                        </div>
                        {/* No of concurrent MDFs */}
                        <div className="flex flex-col gap-1 w-[17.25rem]">
                            <label className="text-sm flex gap-1 items-center">
                                <span className="text-redTriangle">*</span>
                                No of concurrent MDFs
                            </label>
                            <input
                                type="number"
                                className="!h-8 !w-full px-2 border border-borderGreyAlt rounded-md focus:outline-none"
                                placeholder="Enter value"
                                onChange={(e) => {
                                    if (Number(e.target.value) >= 0)
                                        setFormData((prev) => ({
                                            ...prev,
                                            no_of_concurrent_mdfs: Number(e.target.value),
                                        }));
                                }}
                                value={formData.no_of_concurrent_mdfs || ''}
                            />
                        </div>
                    </div>
                ))}
            {/* Objective Section */}

            {/* todo: hiding this temporarily */}
            {/* Max Promo Weeks Section */}
            {/* <div className="flex flex-col gap-1">
                <div className="text-sm flex gap-1 items-center">
                    <span className="text-redTriangle">*</span>
                    <span>Max Promo Weeks: </span>
                </div>

                <Input
                    value={maxPromoWeeks}
                    onChange={(e) => setMaxPromoWeeks(Number(e.target.value))}
                    className="!rounded"
                    type="number"
                />
            </div> */}
        </div>
    );
};

export default React.memo(OptimizePlanForm);
